import { useState, useEffect, useRef } from "react";
import {
  doc,
  updateDoc,
  Timestamp,
  onSnapshot,
  setDoc,
} from "firebase/firestore";
import {
  updateEmail,
  updateProfile,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db } from "../firebaseConfig";
import { useAuth } from "../context/AuthContext";
import { formatShortName } from "../hooks/useUserAvatar";
import ModalShell from "./ModalShell";
import EditProfileForm from "./EditProfileForm";
import EditPersonalForm from "./EditPersonalForm";
import NotificationsPortal, { notify } from "./NotificationsPortal";
import { useTranslation } from "react-i18next";
import { useAvatarUpload } from "../hooks/useUserAvatar";
import {
  Camera,
  Mail,
  Phone,
  Briefcase,
  Building2,
  Cake,
  Pencil,
  X,
  Save,
  Shield,
  Eye,
  EyeOff,
  Info,
  User,
  Trash2,
} from "lucide-react";
import Swal from "sweetalert2";

export default function PerfilPanel() {
  const { t } = useTranslation();
  const avatarFileRef = useRef(null);
  const {
    user,
    firstName,
    lastName,
    userRole,
    avatarUrl,
    setAvatarUrl,
    persistAvatarUrl,
  } = useAuth();
  const { uploadAvatar, uploading } = useAvatarUpload({
    user,
    setAvatarUrl,
    persistAvatarUrl,
  });
  const [imgError, setImgError] = useState(false);
  const [userDoc, setUserDoc] = useState(null); // users/{uid}
  const photoUrlRaw = (avatarUrl || userDoc?.avatarUrl || "").trim();
  const photoUrl =
    photoUrlRaw && photoUrlRaw !== "null" && photoUrlRaw !== "undefined"
      ? photoUrlRaw
      : "";
  const hasPhoto = Boolean(photoUrl) && !imgError;
  const [personalDoc, setPersonalDoc] = useState(null); // users/{uid}/private/personal
  const [whichModal, setWhichModal] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [editingInfo, setEditingInfo] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({
    nombre: "",
    apellido: "",
    telefono: "",
    puesto: "",
    departamento: "",
    email: "",
    nacimiento: "",
    visible: true,
  });

  const birthdayVisible = Boolean(infoForm.visible);
  const close = () => setWhichModal(null);

  async function ensureRecentLogin(user, t) {
    try {
      await updateProfile(user, {});
      return true;
    } catch (e) {
      if (e.code !== "auth/requires-recent-login") throw e;
      const pwd = window.prompt(
        t("auth.reauthPrompt", "Por seguridad, ingresa tu contraseña actual:"),
      );
      if (!pwd) return false;
      const cred = EmailAuthProvider.credential(user.email, pwd);
      await reauthenticateWithCredential(user, cred);
      return true;
    }
  }

  const toInputDate = (v) => {
    const d = v?.toDate
      ? v.toDate()
      : v instanceof Date
        ? v
        : v
          ? new Date(v)
          : null;
    if (!d || isNaN(d)) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatDMY = (v) => {
    const d = v?.toDate
      ? v.toDate()
      : v instanceof Date
        ? v
        : v
          ? new Date(v)
          : null;
    if (!d || isNaN(d)) return "—";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const formatDayMonth = (v) => {
    const d = v?.toDate
      ? v.toDate()
      : v instanceof Date
        ? v
        : v
          ? new Date(v)
          : null;
    if (!d || isNaN(d)) return "—";
    return d.toLocaleDateString("es-MX", { day: "numeric", month: "long" });
  };

  const handleDeleteAvatar = async () => {
    const currentUrl = (
      userDoc?.avatarUrl ||
      avatarUrl ||
      user?.photoURL ||
      ""
    ).trim();
    if (!currentUrl) return;

    const res = await Swal.fire({
      title: "¿Eliminar foto?",
      text: "Se eliminará tu foto de perfil y no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    });

    if (!res.isConfirmed) return;

    // ✅ 0) Backup por si falla
    const prevAvatar = currentUrl;
    const prevUserDocAvatar = userDoc?.avatarUrl || "";

    // ✅ 1) UI INMEDIATA (sin foto)
    setImgError(false);
    setAvatarUrl(""); // 👈 sin avatar2, queda vacío
    setUserDoc((p) => (p ? { ...p, avatarUrl: "" } : p)); // 👈 forzar render inmediato

    try {
      // 2) borrar de Storage (best effort)
      try {
        const storage = getStorage();
        const fileRef = storageRef(storage, currentUrl);
        await deleteObject(fileRef);
      } catch (e) {
        // a veces falla si la url no corresponde al path; no tumbar la UX
        console.warn("[deleteObject avatar]", e);
      }

      // 3) limpiar en Firestore (usa "" NO null)
      await updateDoc(doc(db, "users", user.uid), { avatarUrl: "" });

      // 4) limpiar en Auth (usa "" NO null)
      await updateProfile(user, { photoURL: "" });

      notify("success", "Foto eliminada.");
    } catch (e) {
      console.error(e);

      // ✅ 5) Rollback UI si falló (opcional pero recomendado)
      setAvatarUrl(prevAvatar);
      setUserDoc((p) => (p ? { ...p, avatarUrl: prevUserDocAvatar } : p));

      notify("error", `No se pudo eliminar: ${e.message}`);
    }
  };

  // ✅ 1) Listener users/{uid}
  useEffect(() => {
    if (!user?.uid) return;

    const refUser = doc(db, "users", user.uid);
    const unsub = onSnapshot(
      refUser,
      (snap) => setUserDoc(snap.exists() ? snap.data() : null),
      (err) => {
        if (err?.code === "permission-denied") return; // esperado cuando auth cambia
        console.error("[PerfilPanel users/{uid}]", err);
      },
    );

    return () => unsub();
  }, [user?.uid]);

  // ✅ 2) Listener users/{uid}/private/personal
  useEffect(() => {
    if (!user?.uid) return;

    const refPersonal = doc(db, "users", user.uid, "private", "personal");
    const unsub = onSnapshot(
      refPersonal,
      (snap) => setPersonalDoc(snap.exists() ? snap.data() : null),
      (err) => {
        if (err?.code === "permission-denied") return;
        console.error("[PerfilPanel private/personal]", err);
      },
    );

    return () => unsub();
  }, [user?.uid]);

  // ✅ hidratar form (desde users/{uid}) cuando NO está editando
  useEffect(() => {
    if (!userDoc || editingInfo) return;

    setInfoForm({
      nombre: userDoc?.nombre ?? firstName ?? "",
      apellido: userDoc?.apellido ?? lastName ?? "",
      telefono: userDoc?.telefono ?? "",
      puesto: userDoc?.puesto ?? "",
      departamento: userDoc?.departamento ?? "",
      email: (userDoc?.email ?? user?.email ?? "").trim(),
      nacimiento: userDoc?.nacimiento ? toInputDate(userDoc.nacimiento) : "",
      visible: userDoc?.visible ?? true,
    });
  }, [userDoc, editingInfo, firstName, lastName, user?.email]);

  const startEditInfo = () => setEditingInfo(true);
  const cancelEditInfo = () => setEditingInfo(false);

  // ✅ guarda inline en users/{uid} (NO en usuarios)
  const saveInfoInline = async () => {
    try {
      setSavingInfo(true);

      const userRef = doc(db, "users", user.uid);

      // nacimiento -> Timestamp
      let nacimientoTs = null;
      if (infoForm.nacimiento) {
        const [y, m, d] = infoForm.nacimiento.split("-").map(Number);
        nacimientoTs = Timestamp.fromDate(new Date(y, m - 1, d, 12, 0, 0));
      }

      // 1) Firestore users/{uid}
      await updateDoc(userRef, {
        nombre: (infoForm.nombre || "").trim(),
        apellido: (infoForm.apellido || "").trim(),
        telefono: (infoForm.telefono || "").trim(),
        puesto: (infoForm.puesto || "").trim(),
        departamento: (infoForm.departamento || "").trim(),
        // opcional: conservar email “de referencia” (no es fuente de verdad)
        email: (infoForm.email || "").trim(),
        nacimiento: nacimientoTs,
        visible: Boolean(infoForm.visible),
      });

      // 2) Auth displayName
      const displayName = `${infoForm.nombre} ${infoForm.apellido}`.trim();
      await updateProfile(user, { displayName });

      // 3) Auth email (si cambió)
      const newEmail = (infoForm.email || "").trim();
      if (newEmail && newEmail !== user.email) {
        try {
          const ok = await ensureRecentLogin(user, t);
          if (ok) await updateEmail(user, newEmail);
        } catch {
          notify(
            "error",
            t(
              "toasts.emailReauthNeeded",
              "El correo no se actualizó (se requiere reautenticación). El resto sí se guardó.",
            ),
          );
        }
      }

      notify("success", t("toasts.infoUpdated", "Información actualizada."));
      setEditingInfo(false);
    } catch (e) {
      console.error(e);
      notify("error", `Error al guardar: ${e.message}`);
    } finally {
      setSavingInfo(false);
    }
  };

  if (!user) return null;

  // ✅ “role” ya debe venir del member doc (orgs/{orgId}/members/{uid})
  const role = userRole || "Ventas";
  const email = user?.email || "—";

  const fullName = formatShortName({
    nombre: firstName,
    apellido: lastName,
    displayName: user?.displayName,
    email: user?.email,
  });

  // valores de visualización
  const nombre = userDoc?.nombre ?? firstName ?? "";
  const apellido = userDoc?.apellido ?? lastName ?? "";
  const telefono = userDoc?.telefono ?? "—";
  const puesto = userDoc?.puesto ?? "—";
  const departamento = userDoc?.departamento ?? "—";
  const nacimiento = userDoc?.nacimiento ?? null;

  // ✅ iniciales de forms modales
  const initialProfile = {
    userId: user?.uid,
    name: fullName || "",
    email: user?.email || "",
    role: role || "Ventas",
    photoFile: null,
  };

  // ✅ ahora los datos sensibles salen de private/personal
  const initialPersonal = {
    birth: nacimiento ? toInputDate(nacimiento) : "",
    phone: userDoc?.telefono ?? "",
    curp: personalDoc?.curp ?? "",
    rfc: personalDoc?.rfc ?? "",
    nss: personalDoc?.nss ?? "",
  };

  // ✅ guardar en nuevo modelo
  const save = (kind) => async (data) => {
    try {
      const userRef = doc(db, "users", user.uid);
      const personalRef = doc(db, "users", user.uid, "private", "personal");

      if (kind === "perfil") {
        setSavingProfile(true);

        // Foto
        let photoURL = user.photoURL || null;
        if (data.photoFile) {
          const storage = getStorage();
          const path = `avatars/${user.uid}/${Date.now()}-${data.photoFile.name}`;
          const ref = storageRef(storage, path);
          await uploadBytes(ref, data.photoFile);
          photoURL = await getDownloadURL(ref);

          await updateDoc(userRef, { avatarUrl: photoURL });
          setAvatarUrl(photoURL);
        }

        // Nombre / apellido / email en users/{uid}
        const [n, ...rest] = (data.name || "").trim().split(/\s+/);
        const a = rest.join(" ");

        await updateDoc(userRef, {
          nombre: n || "",
          apellido: a || "",
          email: (data.email || "").trim(),
        });

        // Auth profile
        await updateProfile(user, {
          displayName: data.name,
          photoURL: photoURL || user.photoURL || null,
        });

        // Auth email
        if ((data.email || "").trim() && data.email.trim() !== user.email) {
          try {
            const ok = await ensureRecentLogin(user, t);
            if (ok) await updateEmail(user, data.email.trim());
          } catch {
            notify(
              "error",
              t(
                "toasts.emailReauthNeeded",
                "El correo no se actualizó (se requiere reautenticación). El resto sí se guardó.",
              ),
            );
          }
        }

        notify(
          "success",
          t("toasts.profileUpdated", "Perfil actualizado correctamente."),
        );
      }

      if (kind === "personal") {
        let nacimientoTs = null;
        if (data.birth) {
          const [y, m, d] = data.birth.split("-").map(Number);
          nacimientoTs = Timestamp.fromDate(new Date(y, m - 1, d, 12, 0, 0));
        }

        // ✅ nacimiento y teléfono siguen en users/{uid}
        await updateDoc(userRef, {
          nacimiento: nacimientoTs,
          telefono: (data.phone || "").trim(),
        });

        // ✅ curp/rfc/nss a private/personal (doc)
        await setDoc(
          personalRef,
          {
            curp: (data.curp || "").trim().toUpperCase(),
            rfc: (data.rfc || "").trim().toUpperCase(),
            nss: (data.nss || "").trim(),
          },
          { merge: true },
        );

        notify(
          "success",
          t("toasts.personalUpdated", "Información personal actualizada."),
        );
      }

      close();
    } catch (e) {
      console.error(e);
      notify(
        "error",
        t("toasts.saveError", {
          defaultValue: "Error al guardar: {{msg}}",
          msg: e.message,
        }),
      );
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <>
      <NotificationsPortal />

      <div className="config-section">
        <div className="profile-wrap">
          <div className="profile-left">
            <div className="profile-avatar">
              {/* círculo (recorta imagen) */}
              <div className="profile-avatar-media">
                {hasPhoto ? (
                  <img
                    className="profile-avatar-img"
                    src={photoUrl}
                    alt="Avatar"
                    onError={() => setImgError(true)} // ✅ si falla, muestra icono
                  />
                ) : (
                  <div
                    className="profile-avatar-fallback"
                    aria-label="Sin foto de perfil"
                  >
                    <User size={46} />
                  </div>
                )}
              </div>

              {/* Acciones (alineadas a la derecha) */}
              <div
                className={`profile-avatar-actions ${hasPhoto ? "has-two" : "has-one"}`}
              >
                {/* ✅ Basura SOLO si hay foto real */}
                {hasPhoto && (
                  <button
                    type="button"
                    className="profile-avatar-btn profile-avatar-btn--danger"
                    title="Eliminar foto"
                    onClick={handleDeleteAvatar}
                    disabled={uploading}
                  >
                    <Trash2 size={16} />
                  </button>
                )}

                {/* Cámara SIEMPRE */}
                <button
                  type="button"
                  className="profile-avatar-btn"
                  title={t("profile.changePhoto")}
                  disabled={uploading}
                  onClick={() => {
                    setImgError(false); // ✅ por si antes falló una carga
                    avatarFileRef.current?.click();
                  }}
                >
                  <Camera size={16} />
                </button>
              </div>

              <input
                ref={avatarFileRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;

                  // ✅ preview instantáneo
                  const previewUrl = URL.createObjectURL(file);
                  setImgError(false);
                  setAvatarUrl(previewUrl);

                  try {
                    await uploadAvatar(file); // aquí tu hook ya debe sustituir por la URL final
                    notify(
                      "success",
                      t("toasts.photoUpdated", "Foto actualizada."),
                    );
                  } catch (err) {
                    // si falla, quitamos preview
                    setAvatarUrl("");
                    console.error(err);
                    notify(
                      "error",
                      t(
                        "toasts.photoUpdateError",
                        "No se pudo actualizar la foto.",
                      ),
                    );
                  } finally {
                    // liberar memoria del preview
                    URL.revokeObjectURL(previewUrl);
                  }
                }}
              />
            </div>

            <div className="profile-id">
              <div className="profile-name">{fullName}</div>
              <div className="profile-role">
                <span className="profile-role-pill">
                  {t(`roles.${role}`, role)}
                </span>
              </div>
            </div>
          </div>

          <div className="profile-right">
            <div className="profile-mini-grid">
              <div className="profile-mini">
                <div className="profile-mini-label">
                  <Mail size={16} />{" "}
                  {t("profile.emailLabel", "Correo electrónico")}
                </div>
                <div className="profile-mini-value">{email}</div>
              </div>

              <div className="profile-mini">
                <div className="profile-mini-label">
                  <Phone size={16} /> {t("personal.phone", "Teléfono")}
                </div>
                <div className="profile-mini-value">{telefono}</div>
              </div>

              <div className="profile-mini">
                <div className="profile-mini-label">
                  <Briefcase size={16} /> {t("profile.position", "Puesto")}
                </div>
                <div className="profile-mini-value">{puesto}</div>
              </div>

              <div className="profile-mini">
                <div className="profile-mini-label">
                  <Building2 size={16} />{" "}
                  {t("profile.department", "Departamento")}
                </div>
                <div className="profile-mini-value">{departamento}</div>
              </div>

              {birthdayVisible && (
                <div className="profile-mini">
                  <div className="profile-mini-label">
                    <Cake size={16} /> {t("profile.birthday", "Cumpleaños")}
                  </div>
                  <div className="profile-mini-value">
                    {formatDayMonth(nacimiento)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ✅ CONTENEDOR 2: INFORMACIÓN ADICIONAL */}
        <div
          className={`info-wrap ${editingInfo ? "is-editing" : "is-readonly"}`}
        >
          <div className="info-head">
            <div className="info-title">
              {t("profile.additionalInfo", "Información Adicional")}
            </div>

            {!editingInfo ? (
              <button
                type="button"
                className="info-edit"
                onClick={startEditInfo}
              >
                <Pencil size={16} />
                <span>{t("actions.edit", "Editar")}</span>
              </button>
            ) : (
              <div className="info-actions">
                <button
                  type="button"
                  className="info-cancel"
                  onClick={cancelEditInfo}
                  disabled={savingInfo}
                >
                  <X size={16} /> {t("actions.cancel", "Cancelar")}
                </button>
                <button
                  type="button"
                  className="info-save"
                  onClick={saveInfoInline}
                  disabled={savingInfo}
                >
                  <Save size={16} /> {t("actions.save", "Guardar")}
                </button>
              </div>
            )}
          </div>

          <div className="info-divider" />

          <div className="info-grid">
            <div className="info-field">
              <label>
                {t("profile.firstName", "Nombre")}
                <span style={{ color: "red", marginLeft: "3px" }}>*</span>
              </label>
              {!editingInfo ? (
                <div className="info-input">{nombre || "—"}</div>
              ) : (
                <div className="info-input">
                  <input
                    className="info-input-control"
                    value={infoForm.nombre}
                    onChange={(e) =>
                      setInfoForm((p) => ({ ...p, nombre: e.target.value }))
                    }
                  />
                </div>
              )}
            </div>

            <div className="info-field">
              <label>
                {t("profile.lastName", "Apellido")}
                <span style={{ color: "red", marginLeft: "3px" }}>*</span>
              </label>

              {!editingInfo ? (
                <div className="info-input">{apellido || "—"}</div>
              ) : (
                <div className="info-input">
                  <input
                    className="info-input-control"
                    value={infoForm.apellido}
                    onChange={(e) =>
                      setInfoForm((p) => ({ ...p, apellido: e.target.value }))
                    }
                  />
                </div>
              )}
            </div>

            <div className="info-field">
              <label>{t("profile.telephone", "Teléfono")}</label>

              {!editingInfo ? (
                <div className="info-input">
                  <Phone size={16} />
                  <span>{telefono}</span>
                </div>
              ) : (
                <div className="info-input">
                  <Phone size={16} />
                  <input
                    className="info-input-control"
                    value={infoForm.telefono}
                    onChange={(e) =>
                      setInfoForm((p) => ({ ...p, telefono: e.target.value }))
                    }
                  />
                </div>
              )}
            </div>

            <div className="info-field">
              <label>{t("profile.position", "Puesto")}</label>

              {!editingInfo ? (
                <div className="info-input">{puesto}</div>
              ) : (
                <div className="info-input">
                  <input
                    className="info-input-control"
                    value={infoForm.puesto}
                    onChange={(e) =>
                      setInfoForm((p) => ({ ...p, puesto: e.target.value }))
                    }
                  />
                </div>
              )}
            </div>

            <div className="info-field">
              <label>{t("profile.department", "Departamento")}</label>

              {!editingInfo ? (
                <div className="info-input">{departamento}</div>
              ) : (
                <div className="info-input">
                  <input
                    className="info-input-control"
                    value={infoForm.departamento}
                    onChange={(e) =>
                      setInfoForm((p) => ({
                        ...p,
                        departamento: e.target.value,
                      }))
                    }
                  />
                </div>
              )}
            </div>

            <div className="info-field">
              <label>
                {t("profile.emailLabel", "Correo electrónico")}
                <span style={{ color: "red", marginLeft: "3px" }}>*</span>
              </label>
              {!editingInfo ? (
                <div className="info-input">
                  <Mail size={16} />
                  <span>{email}</span>
                </div>
              ) : (
                <div className="info-input">
                  <Mail size={16} />
                  <input
                    className="info-input-control"
                    value={infoForm.email}
                    onChange={(e) =>
                      setInfoForm((p) => ({ ...p, email: e.target.value }))
                    }
                  />
                </div>
              )}
            </div>

            <div className="info-field">
              <label>
                {t("profile.birthDate", "Fecha de Nacimiento")}
                <span style={{ color: "red", marginLeft: "3px" }}>*</span>
              </label>
              {!editingInfo ? (
                <div className="info-input">
                  <Cake size={16} />
                  <span>{formatDMY(nacimiento)}</span>
                </div>
              ) : (
                <div className="info-input">
                  <Cake size={16} />
                  <input
                    type="date"
                    className="info-input-control"
                    value={infoForm.nacimiento}
                    onChange={(e) =>
                      setInfoForm((p) => ({ ...p, nacimiento: e.target.value }))
                    }
                  />
                </div>
              )}
            </div>

            <div className="info-field">
              <label>{t("account.role", "Rol")}</label>
              <div className="info-input info-input-readonly info-role">
                <Shield size={16} />
                <span>{t(`roles.${role}`, role)}</span>
              </div>
            </div>
          </div>

          <div className="info-foot">
            <div
              className={`info-visibility ${!editingInfo ? "is-disabled" : ""}`}
            >
              <div className="info-visibility-left">
                {birthdayVisible ? (
                  <Eye size={16} style={{ color: "var(--accent, #3B82F6)" }} />
                ) : (
                  <EyeOff
                    size={16}
                    style={{ color: "var(--accent, #3B82F6)" }}
                  />
                )}
                <span>
                  {birthdayVisible
                    ? t(
                        "profile.birthdayVisible",
                        "Visible para otros usuarios",
                      )
                    : t("profile.birthdayHidden", "Oculto para otros usuarios")}
                </span>
                <span
                  className="info-tip"
                  tabIndex={0}
                  role="button"
                  aria-label={t(
                    "profile.birthdayVisibilityHelp",
                    "Controla quién puede ver tu fecha de cumpleaños en tu perfil",
                  )}
                >
                  <Info size={16} />
                  <span className="info-tip-bubble" role="tooltip">
                    {t(
                      "profile.birthdayVisibilityHelp",
                      "Controla quién puede ver tu fecha de cumpleaños en tu perfil",
                    )}
                  </span>
                </span>
              </div>

              <label className="info-switch">
                <input
                  type="checkbox"
                  checked={birthdayVisible}
                  disabled={!editingInfo}
                  onChange={(e) =>
                    setInfoForm((p) => ({ ...p, visible: e.target.checked }))
                  }
                />
                <span className="info-slider" />
              </label>
            </div>
          </div>
        </div>

        {/* Modales */}
        <ModalShell
          isOpen={Boolean(whichModal)}
          onClose={close}
          title={
            whichModal === "profile"
              ? t("modals.editProfile", "Editar perfil")
              : whichModal === "personal"
                ? t("modals.editPersonal", "Editar información personal")
                : ""
          }
        >
          {whichModal === "profile" && (
            <EditProfileForm
              initial={initialProfile}
              onSubmit={save("perfil")}
              onCancel={close}
              saving={savingProfile}
            />
          )}

          {whichModal === "personal" && (
            <EditPersonalForm
              initial={initialPersonal}
              onSubmit={save("personal")}
              onCancel={close}
            />
          )}
        </ModalShell>
      </div>
    </>
  );
}
